/**
 * Aba Geral em modo leitura (PR1 · Foundation).
 *
 * Renderiza apenas campos com fonte REAL no serializer (R15):
 * name · goal · successCriteria · vertical · status · version · createdAt/updatedAt
 * · ethicalBoundaries (redirectTopics + toneLimits).
 *
 * Campos do mockup SEM fonte no backend (description, idioma, tags, persona por
 * nome, "ver diff"/versão-anterior) são deliberadamente omitidos — zero dado
 * falso. A edição (gate `playbook-author`) chega em fatia futura.
 */

import { verticalLabel } from '../playbook-labels';
import { PlaybookStatusPill } from '../PlaybookStatusPill';

import type { PlaybookDto } from '../types';

function formatDateTime(iso: string | null): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function TabGeneralRead({ playbook }: { playbook: PlaybookDto }): JSX.Element {
  const { redirectTopics, toneLimits } = playbook.ethicalBoundaries;

  return (
    <div className="pb-panel" role="tabpanel" aria-label="Geral">
      <h2 className="pb-panel__title">Geral</h2>

      <div className="pb-field">
        <span className="pb-field__label">Nome do playbook</span>
        <p className="pb-read-value">{playbook.name}</p>
      </div>

      <div className="pb-field">
        <span className="pb-field__label">Objetivo</span>
        <p className="pb-read-value">{playbook.goal !== '' ? playbook.goal : '—'}</p>
      </div>

      <div className="pb-field">
        <span className="pb-field__label">Critério de sucesso</span>
        <p className="pb-read-value">
          {playbook.successCriteria !== '' ? playbook.successCriteria : '—'}
        </p>
      </div>

      <div className="pb-grid-2">
        <div className="pb-field">
          <span className="pb-field__label">Vertical</span>
          <p className="pb-read-value">{verticalLabel(playbook.vertical)}</p>
        </div>
        <div className="pb-field">
          <span className="pb-field__label">Status</span>
          <div>
            <PlaybookStatusPill status={playbook.status} />
          </div>
        </div>
      </div>

      <div className="pb-panel__section">
        <span className="pb-panel__section-label">Limites éticos</span>
        <div className="pb-field">
          <span className="pb-field__label">Tópicos de redirecionamento</span>
          {redirectTopics.length > 0 ? (
            <div className="pb-chip-row">
              {redirectTopics.map((topic) => (
                <span key={topic} className="pb-chip">
                  {topic}
                </span>
              ))}
            </div>
          ) : (
            <p className="pb-read-value pb-read-value--muted">Nenhum definido</p>
          )}
        </div>
        <div className="pb-field">
          <span className="pb-field__label">Limites de tom</span>
          {toneLimits.length > 0 ? (
            <ul className="pb-read-list">
              {toneLimits.map((limit) => (
                <li key={limit}>{limit}</li>
              ))}
            </ul>
          ) : (
            <p className="pb-read-value pb-read-value--muted">Nenhum definido</p>
          )}
        </div>
      </div>

      <div className="pb-panel__divider" />

      <div className="pb-meta-row">
        <span>
          Versão atual: <strong>v{playbook.version}</strong>
        </span>
        <span>Criado em {formatDateTime(playbook.createdAt)}</span>
        <span>Atualizado em {formatDateTime(playbook.updatedAt)}</span>
      </div>
    </div>
  );
}
